import { useMemo } from "react";
import { Row } from "reactstrap";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import { StatWidget } from "./components/StatWidget";
import type { DashboardSummaryData } from "./types/dashboard.types";
import { formatDashboardCount } from "./utils/formatDashboardSummary";

type Props = {
  summary: DashboardSummaryData | null;
  loading: boolean;
  error: string | null;
};

export function Widgets({ summary, loading, error }: Props) {
  const { can } = useAccess();

  const canReadWorkOrders = can(Permissions.WORK_ORDER.READ);
  const canReadEnquiries = can(Permissions.ENQUIRY.READ);
  const canReadEmployees = can(Permissions.EMPLOYEE.READ);
  const canReadAttendance = can(Permissions.EMPLOYEE_ATTENDANCE.READ);
  const canReadQuotations = can(Permissions.QUOTATION.READ);

  const widgets = useMemo(() => {
    const sub = loading ? "Loading…" : (error ?? "—");
    const list: Array<{
      title: string;
      value: string;
      subtext: string;
      icon: string;
      color: string;
      to?: string;
    }> = [];

    if (canReadWorkOrders) {
      list.push({
        title: "Work orders",
        value: summary
          ? formatDashboardCount(summary.workOrdersInProgress)
          : "—",
        subtext: summary ? "In progress" : sub,
        icon: "briefcase",
        color: "success",
        to: "/work-orders",
      });
    }

    if (canReadEnquiries) {
      list.push({
        title: "Pending enquiries",
        value: summary ? formatDashboardCount(summary.pendingEnquiries) : "—",
        subtext: summary ? "Require follow-up" : sub,
        icon: "mail",
        color: "warning",
        to: "/enquiry-management",
      });
    }

    if (canReadQuotations) {
      list.push({
        title: "Pending quotations",
        value: summary
          ? formatDashboardCount(summary.pendingQuotations ?? 0)
          : "—",
        subtext: summary ? "Draft quotations" : sub,
        icon: "file-list-3",
        color: "info",
        to: "/quotations",
      });
    }

    if (canReadEmployees && (!summary || summary.totalEmployees !== null)) {
      list.push({
        title: "Total employees",
        value: summary
          ? formatDashboardCount(summary.totalEmployees ?? 0)
          : "—",
        subtext: summary ? "Active employee profiles" : sub,
        icon: "group",
        color: "primary",
        to: "/employees",
      });
    }

    if (
      canReadAttendance &&
      (!summary || summary.employeesPresentToday !== null)
    ) {
      list.push({
        title: "Employees present",
        value: summary
          ? formatDashboardCount(summary.employeesPresentToday ?? 0)
          : "—",
        subtext: summary ? "Employees clocked in today" : sub,
        icon: "user-follow",
        color: "success",
        to: "/hr-management/attendance-sheet",
      });
    }

    return list;
  }, [
    summary,
    loading,
    error,
    canReadWorkOrders,
    canReadEnquiries,
    canReadQuotations,
    canReadEmployees,
    canReadAttendance,
  ]);

  if (widgets.length === 0) return null;

  return (
    <Row>
      {widgets.map((w) => (
        <StatWidget key={w.title} {...w} />
      ))}
    </Row>
  );
}
